import type { SessionResponse } from '@evesix/contracts';
import type { PinVerification } from '../domain/types';
import { ApiRequestError, type ApiClient } from './api';

/**
 * Whether the operator's completed cycles count as cleaning. An API from before the flag sends none:
 * then only service accounts are treated as testers, so no hospital operator's work goes unrecorded.
 */
export function realCyclesOf(operator: SessionResponse['operator']): boolean {
  return (operator.realCycles as boolean | undefined) ?? !operator.service;
}

export interface AuthService {
  verifyPin(pin: string): Promise<PinVerification>;
  signOut(): Promise<void>;
}

/**
 * Accepts a single configured PIN. For demos and local development only — production PINs must be
 * verified server-side, never compared in the browser.
 */
export class DemoAuthService implements AuthService {
  readonly #pin: string;
  readonly #operatorName: string;

  constructor(pin: string, operatorName: string) {
    this.#pin = pin;
    this.#operatorName = operatorName;
  }

  verifyPin(pin: string): Promise<PinVerification> {
    return Promise.resolve(
      pin === this.#pin
        ? { ok: true, operator: { name: this.#operatorName, realCycles: true } }
        : { ok: false, reason: 'invalid' },
    );
  }

  signOut(): Promise<void> {
    return Promise.resolve();
  }
}

/** Verifies PINs against the control API, which enforces per-unit lockout. */
export class HttpAuthService implements AuthService {
  readonly #client: ApiClient;
  readonly #unitId: string;

  constructor(client: ApiClient, unitId: string) {
    this.#client = client;
    this.#unitId = unitId;
  }

  async verifyPin(pin: string): Promise<PinVerification> {
    try {
      const session = await this.#client.post<SessionResponse>('/sessions', {
        unitId: this.#unitId,
        pin,
      });
      this.#client.setToken(session.token);
      return {
        ok: true,
        operator: { name: session.operator.name, realCycles: realCyclesOf(session.operator) },
      };
    } catch (error) {
      if (error instanceof ApiRequestError) {
        if (error.code === 'invalid_pin') return { ok: false, reason: 'invalid' };
        if (error.code === 'locked') {
          return { ok: false, reason: 'locked', retryAfterSeconds: error.retryAfterSeconds ?? 900 };
        }
      }
      throw error; // network or server trouble → reported as "unavailable"
    }
  }

  async signOut(): Promise<void> {
    if (!this.#client.signedIn) return;
    try {
      await this.#client.delete('/sessions/current');
    } finally {
      this.#client.setToken(null);
    }
  }
}
